import {
  ResponseDto,
  SurveyResponseDto,
} from '@act/shared/data-transfer-objects';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { legacySurveyRequests } from '../requests/legacy-survey.requests';
import { useAppSelector } from '../store-hooks';
import { SurveyState } from '../types/surveyTypes';
import { createApiThunk } from '../utils/api-thunk';
import { feedSlice, getFeedItems } from './feedSlice';
import { resetRedux } from '../actions/shared-actions';

// THUNKS/ACTIONS ==============================================================
export const getResponse = createApiThunk(legacySurveyRequests.getResponse);
export const getUserResponses = createApiThunk(
  legacySurveyRequests.getUserResponses
);
export const createNewSurveyAnswer = createApiThunk(
  legacySurveyRequests.postNewResponseAnswer
);
export const deleteSurveyAnswer = createApiThunk(
  legacySurveyRequests.deleteResponseAnswer
);

// INITIAL STATE ===============================================================
const userResponsesAdapter = createEntityAdapter<ResponseDto>();

const surveyResponseAdapter = createEntityAdapter<SurveyResponseDto>();

const initialState: SurveyState = {
  status: 'initial',
  userResponses: userResponsesAdapter.getInitialState(),
  response: surveyResponseAdapter.getInitialState(),
};

// SLICE =======================================================================
export const legacySurveySlice = createSlice({
  name: 'survey',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getResponse.pending, state => {
        state.status = 'loading';
      })
      .addCase(getResponse.fulfilled, (state, action) => {
        state.status = 'success';
        state.response = surveyResponseAdapter.addOne(
          state.response,
          action.payload
        );
      })
      .addCase(getUserResponses.pending, state => {
        state.status = 'loading';
      })
      .addCase(getUserResponses.fulfilled, (state, action) => {
        state.status = 'success';
        state.userResponses = userResponsesAdapter.addMany(
          state.userResponses,
          action.payload
        );
      })
      // Feed Items
      .addCase(getFeedItems.fulfilled, (state, action) => {
        surveyResponseAdapter.upsertMany(
          state.response,
          action.payload.surveyResponses
        );
      })
      .addCase(feedSlice.actions.feedItemUpdated, (state, action) => {
        if (!action.payload.feedItemModelView.surveyResponses) return;

        surveyResponseAdapter.upsertMany(
          state.response,
          action.payload.feedItemModelView.surveyResponses
        );
      });
  },
});

// REDUCER =====================================================================
export const legacySurveyReducer = legacySurveySlice.reducer;

// SELECTORS ===================================================================
// User Responses =============================
const selectUserResponsesState = state =>
  (state as { legacySurvey: typeof initialState })?.legacySurvey?.userResponses;

export const { selectAll: selectAllUserResponses } =
  userResponsesAdapter.getSelectors(selectUserResponsesState);

// User Response =============================
const selectResponseState = state =>
  (state as { legacySurvey: typeof initialState })?.legacySurvey?.response;

export const {
  selectAll: selectAllResponses,
  selectById: selectSurveyResponseById,
} = surveyResponseAdapter.getSelectors(selectResponseState);

// EXPORT HELPER ===============================================================
export const useSurveySelectors = {
  useSelectSurveyResponseById: (surveyResponseId: string) =>
    useAppSelector(state => selectSurveyResponseById(state, surveyResponseId)),
};

// EFFECTS =====================================================================
// WEBSOCKET ACTIONS ===========================================================
